import { Parser as Json2CsvParser } from '@json2csv/plainjs'
import downloadCSVAsFile from '../../../utils/downloadCSVAsFile'

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export interface DayHourBreakdown {
  day?: Record<string, number>
  hour?: Record<string, number>
}

interface CsvParams {
  mobilityStats: any
  keyIndicator: string
  keyIndicatorLabel: string
  dayHour?: DayHourBreakdown
}

function buildCampaignCSV(params: CsvParams): string {
  const { mobilityStats, keyIndicator, keyIndicatorLabel, dayHour } = params

  const parseCSV = (data, opts) => new Json2CsvParser(opts).parse(data)
  const label = keyIndicatorLabel.toLowerCase()

  let fields: any[] = mobilityStats.ranking.columns.map((c) => {
    const key = c.key === keyIndicator ? label : c.key
    return { label: capitalize(key), value: key, default: '' }
  })
  fields.push('traffic')

  const rows = mobilityStats.ranking.data?.[0]?.list || []
  const data = rows.map((p) => {
    const value = Number(p[keyIndicator]) || 0
    const range = [
      Math.floor(value / 500000) * 500000,
      Math.ceil(value / 500000) * 500000,
    ]
    return {
      ...p,
      id: String(p.id).split(':').pop(),
      [label]: range.map((v) => new Intl.NumberFormat().format(v)).join(' - '),
    }
  })

  const sampleRow = data[0] || {}

  const generateEmptyLines = (currentFields: any[]) => {
    const emptyLine = Object.keys(sampleRow).reduce((accum, k) => {
      accum[k] = ''
      return accum
    }, {})
    const emptyLines = Array.from({ length: 2 }).map(() => emptyLine)
    return '\n' + parseCSV(emptyLines, { fields: currentFields, header: false })
  }

  let csv = parseCSV(data, { fields })
  csv += generateEmptyLines(fields)

  fields = [
    { label: 'Metric', value: 'category', default: '' },
    { label: 'Value', value: 'value', default: '' },
  ]

  const reachSum = Array.isArray(mobilityStats.visitorsSum)
    ? mobilityStats.visitorsSum[0]
    : mobilityStats.visitorsSum
  const reachAvg = Array.isArray(mobilityStats.visitorsAvg)
    ? mobilityStats.visitorsAvg[0]
    : mobilityStats.visitorsAvg
  const trafficValue = Array.isArray(mobilityStats.traffic)
    ? mobilityStats.traffic[0]
    : mobilityStats.traffic ??
      rows.reduce((sum, p) => sum + (Number(p.traffic) || 0), 0)

  const reachAvgNumber = Number(reachAvg)
  const reachAvgRounded =
    reachAvg == null || Number.isNaN(reachAvgNumber)
      ? reachAvg
      : Math.round(reachAvgNumber * 100) / 100

  const trafficNumber = Number(trafficValue)
  const trafficFloored =
    trafficValue == null || Number.isNaN(trafficNumber)
      ? trafficValue
      : Math.floor(trafficNumber)

  const metricData = [
    { category: 'Total Reach', value: reachSum },
    { category: 'Avg. Reach per POI', value: reachAvgRounded },
    { category: 'Vehicles impacted', value: trafficFloored },
  ]
  csv += '\n' + parseCSV(metricData, { fields })
  csv += generateEmptyLines(fields)

  const refSlice = (val: any) =>
    Array.isArray(val) && Array.isArray(val[0]) ? val[0] : val

  const gender = refSlice(mobilityStats.gender)
  if (gender?.length) {
    fields = [
      { label: 'Gender', value: 'category', default: '' },
      { label: 'Value', value: 'value', default: '' },
    ]
    csv += '\n' + parseCSV(gender, { fields })
    csv += generateEmptyLines(fields)
  }

  const age = refSlice(mobilityStats.age)
  if (age?.length) {
    fields = [
      { label: 'Age', value: 'category', default: '' },
      { label: 'Value', value: 'value', default: '' },
    ]
    csv += '\n' + parseCSV(age, { fields })
    csv += generateEmptyLines(fields)
  }

  const profile = refSlice(mobilityStats.visitor_profile)
  if (profile?.length) {
    fields = [
      { label: 'Profile', value: 'category', default: '' },
      { label: 'Value', value: 'value', default: '' },
    ]
    csv += '\n' + parseCSV(profile, { fields })
    csv += generateEmptyLines(fields)
  }

  if (dayHour?.day && Object.keys(dayHour.day).length) {
    fields = [
      { label: 'Day', value: 'category', default: '' },
      { label: 'Value', value: 'value', default: '' },
    ]
    const dayData = Object.entries(dayHour.day).map(([category, value]) => ({
      category,
      value,
    }))
    csv += '\n' + parseCSV(dayData, { fields })
    csv += generateEmptyLines(fields)
  }

  if (dayHour?.hour && Object.keys(dayHour.hour).length) {
    fields = [
      { label: 'Hour', value: 'category', default: '' },
      { label: 'Value', value: 'value', default: '' },
    ]
    const hourData = Object.entries(dayHour.hour).map(([category, value]) => ({
      category,
      value,
    }))
    csv += '\n' + parseCSV(hourData, { fields })
    csv += generateEmptyLines(fields)
  }

  return csv
}

export function downloadCampaignCSV(
  params: CsvParams & {
    campaignName: string
    campaignId: number | string
  },
) {
  const { campaignName, campaignId, ...csvParams } = params
  const csv = buildCampaignCSV(csvParams)
  downloadCSVAsFile(csv, `campaign-ID${campaignId}-${campaignName}.csv`)
}
